<template>
<div class="chat-bubble-container">
    <transition name="fade-scale">
        <!-- Chat Window for Logged In Users -->
        <section v-if="isOpen && hasChatProfile" class="section-6"  style="z-index: 9999999;">
            <div class="chat-box-div">
                <div class="abubody" v-if="!showFeedbackOverlay">
                    <!-- Header -->
                    <div class="box-top-div">
                        <div class="chat-top-div"><img src="@/assets/images/OhMyMeds_Logo_w.svg" loading="lazy" sizes="56.015625px" srcset="@/assets/images/OhMyMeds_Logo_w.svg 500w, @/assets/images/OhMyMeds_Logo_w.svg 800w, @/assets/images/OhMyMeds_Logo_w.svg 1000w" alt="Logo" class="image-406" style="height:45px">
                        <a href="#" @click.prevent="handleCloseChat" class="link-block-56 w-inline-block"><img src="@/assets/images/icon_Close.svg" loading="lazy" alt="" class="image-407"></a>
                    </div>
                        <!-- Chat Messages -->
                        <div class="chat-body-div">
                            <div class="chat-reco-div" ref="chatContainer" @scroll="handleScroll">
                                <!-- Loading Indicator -->
                                <div v-if="isLoadingHistory" class="loading-history">
                                    <div class="loading-spinner"></div>
                                    <span class="loading-text">Loading messages...</span>
                                </div>
                                <div class="chat-div">
                                    <div class="dialogue-div" v-for="(message, index) in messages" :key="`chat-message-${index}`">
                                        <!-- User Message -->
                                        <div class="user-div" v-if="message.role === 'user'">
                                            <div class="speech">
                                                <div class="text-block-806" v-html="message.content"></div>
                                                <small v-if="message.timestamp" class="message-timestamp">
                                                    {{ formatTimestamp(message.timestamp) }}
                                                </small>
                                            </div>
                                        </div>
                                        <!-- Assistant Message -->
                                        <div class="platform-div" v-else>
                                            <div class="div-block-1757"></div>
                                            <div class="response-div">
                                                <p class="paragraph-132" v-html="message.content"></p>
                                                <small v-if="message.timestamp" class="message-timestamp">
                                                    {{ formatTimestamp(message.timestamp) }}
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Input Area -->
                    <div class="box-bottom-div">
                        <div class="chat-area-div">
                            <div class="form-block-2 w-form">
                                <form class="form-10" @submit.prevent="sendMessage">
                                    <textarea class="text-field-53 w-input" maxlength="256" name="message" rows="2" placeholder="Type your message..." v-model="currentMessage" @keypress.enter="sendMessage"></textarea>
                                    <div class="div-block-1754" @click="sendMessage" :class="{ 'disabled': !canSendMessage }">
                                        <img src="@/assets/images/Image_from_iOS.jpg" loading="lazy" alt="Send" class="image-413">
                                    </div>
                                </form>
                            </div>
                            <div class="chtnote">This is not medical advice. Review our terms for more info.</div>
                        </div>
                    </div>
                </div>

                <!-- Feedback Form View -->
                <div class="feedback-container" v-if="showFeedbackOverlay">
                    <div class="feedback-content">
                        <div class="feedback-header">
                            <h3 class="feedback-title">Overall, how would you rate your chat experience?</h3>
                        </div>

                        <!-- Rating Section -->
                        <div class="rating-section">
                            <button
                                v-for="rating in [1, 2, 3, 4]"
                                :key="rating"
                                class="rating-btn"
                                :class="{ 'active': feedbackForm.rating === rating }"
                                @click="feedbackForm.rating = rating"
                            >
                                <img
                                    :src="getRatingIcon(rating, feedbackForm.rating === rating)"
                                    :alt="`Rating ${rating}`"
                                    class="rating-icon"
                                />
                            </button>
                        </div>

                        <!-- Issues Section - Only show if rating is 1-4 -->
                        <div class="feedback-section" v-if="shouldShowIssuesSection">
                            <h4 class="section-title">What went wrong?</h4>
                            <div class="options-grid">
                                <button
                                    v-for="issue in issueOptions"
                                    :key="issue"
                                    class="option-btn"
                                    :class="{ 'active': feedbackForm.issues.includes(issue) }"
                                    @click="toggleIssue(issue)"
                                >
                                    {{ issue }}
                                </button>
                            </div>
                        </div>

                        <!-- Resolution Section -->
                        <div class="feedback-section">
                            <h4 class="section-title">Did we resolve your issue?</h4>
                            <div class="yes-no-buttons">
                                <button
                                    class="yn-btn"
                                    :class="{ 'active': feedbackForm.resolved === true }"
                                    @click="feedbackForm.resolved = true"
                                >
                                    Yes
                                </button>
                                <button
                                    class="yn-btn"
                                    :class="{ 'active': feedbackForm.resolved === false }"
                                    @click="feedbackForm.resolved = false"
                                >
                                    No
                                </button>
                            </div>
                        </div>

                        <!-- Additional Feedback Section -->
                        <div class="feedback-section">
                            <h4 class="section-title">Add Additional feedback</h4>
                            <textarea
                                v-model="feedbackForm.additionalFeedback"
                                class="feedback-textarea"
                                placeholder="Type your feedback here..."
                                maxlength="320"
                            ></textarea>
                            <div class="char-count">{{ feedbackForm.additionalFeedback.length }}/320</div>
                        </div>

                        <!-- Action Buttons -->
                        <div class="feedback-actions">
                            <button class="btn-submit" @click="submitFeedback" style="color:#fff">Submit Feedback</button>
                            <button class="btn-skip" @click="skipFeedback">Skip</button>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Login Section -->
        <section v-if="isOpen && !hasChatProfile" class="section-6">
            <div class="chat-box-div">
                <div class="box-top-div">
                    <div class="chat-top-div"><img src="@/assets/images/OhMyMeds_Logo_w.svg" loading="lazy" sizes="56.015625px" srcset="@/assets/images/OhMyMeds_Logo_w.svg 500w, @/assets/images/OhMyMeds_Logo_w.svg 800w, @/assets/images/OhMyMeds_Logo_w.svg 1000w" alt="Logo" class="image-406" style="height:45px">
                        <a href="#" @click.prevent="toggleChat" class="link-block-56 w-inline-block"><img src="@/assets/images/icon_Close.svg" loading="lazy" alt="" class="image-407"></a>
                    </div>
                    <div class="chat-body-div">
                        <div class="chat-reco-div">
                            <div class="chat-body-top">
                                <div class="text-block-788">Welcome! 😊</div>
                                <div class="text-block-788" style="font-weight: 300; font-size: 16px;"><br />Enter your name and email to get started. This helps us notify you if we get disconnected.</div>
                            </div>
                            <div class="chat-div">
                                <div class="dialogue-div">
                                    <form @submit.prevent="registerUser">
                                        <div class="_2-input-div city-state">
                                            <input class="tf_textfield-2 half w-input" maxlength="256" v-model="userData.first_name" name="First-Name-2" data-name="First Name 2" placeholder="First Name" type="text" id="First-Name-2" required>
                                            <input class="tf_textfield-2 half w-input" maxlength="256" v-model="userData.last_name" name="Last-Name-2" data-name="Last Name 2" placeholder="Last Name" type="text" id="Last-Name-2" required>
                                        </div>
                                        <input class="tf_textfield-2 w-input" maxlength="256" name="Email-Address-2" v-model="userData.email" data-name="Email Address 2" placeholder="Email Address" type="email" id="Email-Address-2" required>
                                        <div v-if="!submitted"> <input type="submit" class="checkout-button-2 w-button" style="color:#fff;" value="Get Started"> </div>
                                        <div v-else>
                                            <a href="#" class="checkout-button-2 w-button">
                                                <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" style="margin: auto; background: none; display: block; shape-rendering: auto;" width="15px" height="15px" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid">
                                                    <circle cx="50" cy="50" fill="none" stroke="blue" stroke-width="10" r="35" stroke-dasharray="164.93361431346415 56.97787143782138">
                                                        <animateTransform attributeName="transform" type="rotate" repeatCount="indefinite" dur="0.53475935828877s" values="0 50 50;360 50 50" keyTimes="0;1"></animateTransform>
                                                    </circle>
                                                </svg>
                                            </a>
                                        </div>
                                        <div class="text-block-865">By hitting submit you agree to receive reminder emails for chats, upcoming orders, tips, and other emails from {{ appName }}</div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </transition>

    <div class="chat-bubble" v-if="!isOpen && checkIcon" @click="toggleChat">
        <img v-if="!isOpen" src="@/assets/images/image.png" alt="Chat" class="chat-bubble-icon">
        <img v-else src="@/assets/images/icon_Close.svg" alt="Close" class="chat-bubble-icon">
    </div>
</div>
</template>

    
    
<script>
import {
    mapGetters
} from 'vuex';
import {
    io
} from 'socket.io-client';
import {
    format
} from 'date-fns';

export default {
    name: 'ChatWindow',
    props: ["hideIconOn"],

    data() {
        return {
            appName: "OhMyMeds",
            isOpen: false,
            pageOpenedOn: '',
            sessionId: null,
            conversationId: null,
            userId: 'anonymous',
            socket: null,
            messages: [],
            currentMessage: '',
            connectionStatus: 'disconnected',
            loginUrl: process.env.VUE_APP_API_ENDPOINT,
            email: null,
            password: null,
            submitted: false,
            err: false,
            errmsg: "",
            userInitialized: false,
            initializationAttempts: 0,
            maxInitializationAttempts: 3,
            reconnectTimer: null,
            isReconnecting: false,
            socketInitialized: false,
            pendingInitialization: false,
            userData: {
                first_name: '',
                last_name: '',
                email: '',
                source_web: 2,
                user_type: 'chat'
            },
            showFeedbackOverlay: false,
            feedbackForm: {
                rating: null,
                issues: [],
                resolved: null,
                additionalFeedback: ''
            },
            nextCursor: null,
            hasMoreHistory: true,
            isLoadingHistory: false
        }
    },

    computed: {
        ...mapGetters(['user']),

        userDetails() {
            if (this.isLoggedin && this.user) {
                return {
                    id: this.user.id,
                    name: `${this.user.first_name} ${this.user.last_name}`.trim(),
                    email: this.user.email
                };
            } else if (this.$store.getters.hasChatProfile) {
                const chatProfile = this.$store.getters.chatProfile;
                return {
                    id: chatProfile.id || `chat_${Date.now()}`,
                    name: `${chatProfile.first_name} ${chatProfile.last_name}`.trim(),
                    email: chatProfile.email
                };
            }
            return null;
        },

        hasChatProfile() {
            return this.isLoggedin || this.$store.getters.hasChatProfile;
        },

        isLoggedin() {
            return this.$store.getters.isAuthenticated;
        },

        socketUrl() {
            return process.env.VUE_APP_WS_URL || 'http://localhost:3000';
        },

        canSendMessage() {
            return this.socket?.connected &&
                this.currentMessage.trim().length > 0 &&
                !this.isReconnecting;
        },

        checkIcon() {
                // Show icon only on HomePage and not when talkToDoc tab is active
                if (this.$route.name !== 'HomePage') {
                    return false;
                }
                console.log(this.$parent.currentTab)
                // Check if we're on HomePage but in the talkToDoc tab
                // You can access the parent component's currentTab through $parent
                if (this.$store.getters.activeHomeTab === 'doctor') {
                    return false;
                }

                return true;
            },

        issueOptions() {
            return [
                'Took too long',
                'Unhelpful resolution',
                'Lack of expertise',
                'Unfriendly tone',
                'Technical issues',
                'Other'
            ];
        },

        ratingLabel() {
            const labels = {
                1: 'Very Sad',
                2: 'Sad',
                3: 'Happy',
                4: 'Very Happy'
            };
            return this.feedbackForm.rating ? labels[this.feedbackForm.rating] : '';
        },

        shouldShowIssuesSection() {
            // Only show if rating is 1-2 (not 3-4 and not null)
            return this.feedbackForm.rating !== null && this.feedbackForm.rating < 3;
        }
    },

    watch: {
        userDetails: {
            immediate: true,
            handler(newDetails) {
                if (newDetails && !this.socketInitialized) {
                    this.initializeSocket();
                }
            }
        },

        isLoggedin(newValue) {
            if (newValue) {
                this.initializeSocket();
            }
        }
    },

    created() {
        if (window.Tracker) {
            this.initializeFromTracker();
        }
        window.addEventListener('sessionInit', this.handleSessionInit);
        window.addEventListener('userIdentified', this.handleUserIdentified);
        this.$root.$on('toggle-chat', this.toggleChat);
    },
    mounted() {
        if (this.isLoggedin) {
            this.initializeFromTracker();
        }
    },
    beforeDestroy() {
        this.cleanup();
    },

    methods: {
        getRatingIcon(rating, isSelected) {
            try {
                const iconMap = {
                    1: { unselected: 'icon_rating_verysad1.webp', selected: 'icon_rating_verysad2.webp' },
                    2: { unselected: 'icon_rating_sad1.webp', selected: 'icon_rating_sad2.webp' },
                    3: { unselected: 'icon_rating_happy1.webp', selected: 'icon_rating_happy2.webp' },
                    4: { unselected: 'icon_rating_veryhappy1.webp', selected: 'icon_rating_veryhappy2.webp' }
                };

                const icon = iconMap[rating];
                const iconName = isSelected ? icon.selected : icon.unselected;

                // Use direct path for static assets
                return require(`@/assets/rating_icon/${iconName}`);
            } catch (error) {
                console.error('Error loading icon:', error);
                return '';
            }
        },

        async registerUser() {
            try {
                this.submitted = true;
                const response = await this.$axios.post(`${this.loginUrl}/api/chat-user`, this.userData);
                if (response.data.status === 'success') {
                    await this.$store.dispatch("setChatProfile", response.data.user);
                    this.initializeSocket();
                    this.$nextTick(() => {
                        if (this.socket?.connected) {
                            this.initializeUserDetails();
                        } else {
                            console.log('Socket not yet connected after registration');
                        }
                    });
                } else if (response.data.status === 'error' && response.data.message === 'Existing user found.') {
                    this.$router.push({
                        name: 'LoginPage',
                        query: {
                            "page_ref": "Checkouts"
                        }
                    });
                }
                this.submitted = false;
            } catch (error) {
                console.error('Error during registration:', error);
                this.errorMessage = 'An error occurred while creating chat profile.';
                this.submitted = false;
            }
        },
        formatTimestamp(timestamp) {
            if (!timestamp) return '';
            return format(new Date(timestamp), 'HH:mm');
        },

        cleanup() {
            window.removeEventListener('sessionInit', this.handleSessionInit);
            window.removeEventListener('userIdentified', this.handleUserIdentified);
            this.cleanupSocket();
            if (this.reconnectTimer) {
                clearTimeout(this.reconnectTimer);
            }
        },
        initializeFromTracker() {
            if (!this.sessionId) {
                this.sessionId = window.Tracker?.getSessionId() || `session_${Date.now()}`;
            }
            if (this.isLoggedin && this.user?.id) {
                this.userId = this.user.id;
                this.initializeSocket();
            }
        },

        initializeSocket() {
            if (this.socket) {
                this.cleanupSocket();
            }
            const userDetails = this.userDetails;
            if (!userDetails) {
                return;
            }
            try {
                const socketConfig = {
                    transports: ['websocket'],
                    query: {
                        sessionId: this.sessionId || `session_${Date.now()}`,
                        userId: userDetails.id || 'anonymous',
                        page: window.location.href
                    },
                    reconnection: true,
                    reconnectionAttempts: 5,
                    reconnectionDelay: 1000,
                    reconnectionDelayMax: 5000,
                    timeout: 10000
                };
                this.socket = io(this.socketUrl, socketConfig);
                this.socketInitialized = true;
                this.setupSocketListeners();
            } catch (error) {
                this.socketInitialized = false;
            }
        },

        setupSocketListeners() {
            this.socket.on('connect', this.handleConnect);
            this.socket.on('disconnect', this.handleDisconnect);
            this.socket.on('error', this.handleError);
            this.socket.on('chat_message', this.handleChatMessage);
            this.socket.on('history', this.handleHistory);
            this.socket.on('history_more', this.handleHistoryMore);
            this.socket.on('reconnect_attempt', this.handleReconnectAttempt);
            this.socket.on('reconnect_failed', this.handleReconnectFailed);
            this.socket.on('conversation_closed_ok', this.handleConversationClosedOk);
        },

        handleConnect() {
            this.connectionStatus = 'connected';
            this.isReconnecting = false;
            if (this.userDetails) {
                this.initializeUserDetails();
            } else {
                console.log('No user details available yet');
            }

            if (this.isOpen) {
                this.loadChatHistory();
            }
        },

        handleDisconnect() {

            this.connectionStatus = 'disconnected';
            this.startReconnectTimer();
        },

        handleError(error) {
            console.error('Socket.IO error:', error);
            this.connectionStatus = 'error';
            if (error.message === 'User not initialized' &&
                this.initializationAttempts < this.maxInitializationAttempts) {
                this.retryInitialization();
            }
        },

        handleChatMessage(message) {
            console.log(message)
            this.messages.push({
                role: 'assistant',
                content: message.content,
                timestamp: new Date(message.timestamp)
            });
            this.$nextTick(() => {
                this.scrollToBottom();
            });
        },

        handleHistory(history) {
            // Process messages with attachments
            const processedMessages = (history.content || [])
                .filter(msg => msg.role !== 'system')
                .map(msg => ({
                    ...msg,
                    attachments: msg.attachments || [],
                    timestamp: new Date(msg.timestamp)
                }));

            this.messages = processedMessages;

            // Store conversationId from backend
            if (history.conversationId) {
                this.conversationId = history.conversationId;
                console.log('Conversation ID stored:', this.conversationId);
            }

            // Handle pagination info
            this.hasMoreHistory = Boolean(history.hasMore);
            this.nextCursor = history.nextCursor || null;

            this.isLoadingHistory = false;
            this.$nextTick(() => this.scrollToBottom());
        },

        handleHistoryMore(payload) {
            // Get scroll container element
            const container = this.$refs.chatContainer;
            const prevScrollHeight = container?.scrollHeight || 0;

            // Process older messages with attachments
            const olderMessages = (payload.content || [])
                .filter(msg => msg.role !== 'system')
                .map(msg => ({
                    ...msg,
                    attachments: msg.attachments || [],
                    timestamp: new Date(msg.timestamp)
                }));

            // Prepend older messages to the beginning
            this.messages = [...olderMessages, ...this.messages];

            // Update pagination info
            this.hasMoreHistory = Boolean(payload.hasMore);
            this.nextCursor = payload.nextCursor || null;

            this.isLoadingHistory = false;

            // Restore scroll position after messages are prepended
            this.$nextTick(() => {
                if (container) {
                    const newScrollHeight = container.scrollHeight;
                    container.scrollTop = newScrollHeight - prevScrollHeight;
                }
            });
        },

        handleReconnectAttempt(attemptNumber) {
            console.log(attemptNumber)
            this.isReconnecting = true;
        },

        handleReconnectFailed() {

            this.isReconnecting = false;
            this.connectionStatus = 'error';
        },

        handleConversationClosedOk(data) {
            console.log('✓ Conversation closed successfully:', data);
            // Clear the conversationId since conversation is closed
            this.conversationId = null;
            // You can add additional handling here if needed
            // For example, clear messages or show a thank you message
        },

        startReconnectTimer() {
            if (this.reconnectTimer) {
                clearTimeout(this.reconnectTimer);
            }
            this.reconnectTimer = setTimeout(() => {
                if (this.isLoggedin || this.$store.getters.hasChatProfile) {
                    this.initializeSocket();
                }
            }, 5000);
        },

        retryInitialization() {
            setTimeout(() => {
                this.initializeUserDetails();
            }, 1000 * (this.initializationAttempts + 1));
            this.initializationAttempts++;
        },

        cleanupSocket() {
            if (this.socket) {
                this.socket.offAny();
                this.socket.disconnect();
                this.socket = null;
                this.socketInitialized = false;
            }
        },

        handleSessionInit(event) {
            const {
                sessionId,
                userId
            } = event.detail;
            this.sessionId = sessionId;
            this.userId = userId;
            this.initializeSocket();
        },

        handleUserIdentified(event) {
            const {
                newUserId
            } = event.detail;
            this.userId = newUserId;
            this.initializeSocket();
        },

        initializeUserDetails() {

            if (!this.socket?.connected) {

                this.pendingInitialization = true;
                return;
            }
            const userData = this.userDetails;
            if (!userData) {

                return;
            }
            try {
                if (!userData.email) {
                    console.error('Invalid user data:', userData);
                    return;
                }
                this.socket.emit('init_user', userData);
                this.userInitialized = true;
                this.initializationAttempts = 0;
                this.pendingInitialization = false;
                this.setOnlineStatus();

            } catch (error) {
                console.error('Error initializing user details:', error);
                this.pendingInitialization = true;
            }
        },
        setOnlineStatus() {
            setInterval(() => {
                this.socket.to('customer_service_reps').emit('user_online', {
                    userId: this.userData.id,
                    userName: this.userData.name,
                    userEmail: this.userData.email,
                    timestamp: new Date()
                });
            }, 2 * 60 * 1000); // 2 minutes interval
        },
        async sendMessage() {
            if (!this.canSendMessage) return;

            try {
                const userMessage = {
                    role: 'user',
                    content: this.currentMessage.trim(),
                    timestamp: new Date()
                };

                this.messages.push(userMessage);

                this.socket.emit('chat message', {
                    userId: this.userId,
                    message: this.currentMessage.trim()
                });

                this.currentMessage = '';
                this.$nextTick(() => {
                    this.scrollToBottom();
                });

            } catch (error) {
                console.error('Error sending message:', error);
                this.showError('Failed to send message. Please try again.');
            }
        },

        loadChatHistory() {
            if (this.socket?.connected) {
                this.socket.emit('load_history', {});
                this.isLoadingHistory = true;
            }
        },

        loadMoreHistory() {
            if (this.socket?.connected && this.hasMoreHistory && !this.isLoadingHistory && this.conversationId) {
                this.isLoadingHistory = true;
                this.socket.emit('load_more_history', {
                    conversationId: this.conversationId,
                    cursor: this.nextCursor,
                    limit: 25
                });
            }
        },

        handleScroll(event) {
            const container = event.target;
            // Check if user scrolled to the top
            if (container.scrollTop === 0 && this.hasMoreHistory && !this.isLoadingHistory) {
                this.loadMoreHistory();
            }
        },

        async getOrCreateConversation() {
            try {
                // Check if we have a stored conversationId from history
                if (this.conversationId) {
                    return { conversation: { id: this.conversationId } };
                }

                // If not loaded yet, trigger history load and wait
                if (this.socket?.connected) {
                    // Request history which will populate conversationId
                    this.loadChatHistory();

                    // Wait a bit for the history to load
                    await new Promise(resolve => setTimeout(resolve, 500));

                    if (this.conversationId) {
                        return { conversation: { id: this.conversationId } };
                    }
                }

                console.warn('No conversationId available yet');
                return { conversation: null };
            } catch (error) {
                console.error('Error getting conversation:', error);
                return { conversation: null };
            }
        },

        handleCloseChat() {
            // Show feedback overlay if user has messages
            if (this.messages.length > 0) {
                this.showFeedbackOverlay = true;
            } else {
                this.isOpen = false;
            }
        },

        toggleChat() {
            this.isOpen = !this.isOpen;
            if (this.isOpen) {
                this.pageOpenedOn = {
                    path: this.$route.path,
                    fullPath: this.$route.fullPath,
                    name: this.$route.name,
                    query: this.$route.query,
                    params: this.$route.params,
                    url: window.location.href,
                    openedAt: new Date().toISOString()
                };

                if (this.isLoggedin || this.$store.getters.hasChatProfile) {
                    if (!this.socket?.connected) {
                        this.initializeSocket();
                    }
                    this.loadChatHistory();
                    if (!this.userInitialized) {
                        this.initializeUserDetails();
                    }
                }
            }
        },

        toggleIssue(issue) {
            const index = this.feedbackForm.issues.indexOf(issue);
            if (index > -1) {
                this.feedbackForm.issues.splice(index, 1);
            } else {
                this.feedbackForm.issues.push(issue);
            }
        },

        async submitFeedback() {
            try {
                // Check if we have conversationId
                if (!this.conversationId) {
                    console.warn('No conversationId available, attempting to fetch...');
                    const { conversation } = await this.getOrCreateConversation();
                    if (!conversation?.id) {
                        console.error('Failed to get conversationId');
                        this.closeFeedback();
                        return;
                    }
                }

                const feedbackData = {
                    conversationId: this.conversationId,
                    rating: this.feedbackForm.rating,
                    feedback: this.feedbackForm.additionalFeedback || '',
                    wrong: this.feedbackForm.issues.join(', '),  // Convert array to comma-separated string
                    resolved: this.feedbackForm.resolved
                };

                console.log('Closing conversation with feedback:', feedbackData);

                // Send close_conversation event to socket
                if (this.socket && this.socket.connected) {
                    this.socket.emit('close_conversation', feedbackData);
                } else {
                    console.error('Socket not connected');
                }
                

                // Send feedback to your API as backup
                let serverData = {
                    ...this.feedbackForm,
                    conversationId: this.conversationId,
                    userId: this.userDetails?.id,
                    sessionId: this.sessionId,
                    timestamp: new Date().toISOString()
                };
                console.log(JSON.stringify(serverData))
                await this.$axios.post(`${this.loginUrl}/api/chat-feedback`, serverData);

                // Close overlay and chat
                this.closeFeedback();
            } catch (error) {
                console.error('Error submitting feedback:', error);
                // Still close the chat even if feedback submission fails
                this.closeFeedback();
            }
        },

        async skipFeedback() {
            try {
                // Check if we have conversationId
                if (!this.conversationId) {
                    console.warn('No conversationId available when skipping feedback');
                    const { conversation } = await this.getOrCreateConversation();
                    if (conversation?.id) {
                        this.conversationId = conversation.id;
                    }
                }

                // Send close_conversation event without feedback
                if (this.socket && this.socket.connected && this.conversationId) {
                    console.log('Closing conversation without feedback, conversationId:', this.conversationId);
                    this.socket.emit('close_conversation', {
                        conversationId: this.conversationId,
                        rating: null,
                        feedback: '',
                        wrong: '',  // Empty string instead of empty array
                        resolved: null
                    });
                } else {
                    console.error('Cannot close conversation - socket not connected or no conversationId');
                }
            } catch (error) {
                console.error('Error closing conversation:', error);
            }

            this.closeFeedback();
        },

        closeFeedbackWithoutSubmit() {
            // Optional: You can choose to still close or do nothing when clicking outside
            // For now, let's not close to ensure user makes a conscious choice
        },

        closeFeedback() {
            this.showFeedbackOverlay = false;
            this.isOpen = false;
            // Reset feedback form
            this.feedbackForm = {
                rating: null,
                issues: [],
                resolved: null,
                additionalFeedback: ''
            };
        },

        scrollToBottom() {
            const chatContainer = this.$refs.chatContainer;
            if (chatContainer) {
                chatContainer.scrollTop = chatContainer.scrollHeight;
            }
        },

        showError(message) {
            this.err = true;
            this.errmsg = message;
            setTimeout(() => {
                this.err = false;
                this.errmsg = '';
            }, 3000);
        },

        async onSubmit() {
            this.submitted = true;
            try {
                await this.$store.dispatch('login', {
                    email: this.email,
                    password: this.password
                });
                this.submitted = false;
                this.err = false;
            } catch (error) {
                this.submitted = false;
                this.err = true;
                this.errmsg = error.message || 'Login failed. Please try again.';
            }
        }
    }
};
</script>
    
    
<style scoped>

/* Chat container structure */
.section-6 {
    position: fixed;
    inset: 0%;
    z-index: 9999;
}

.chat-box-div {
    flex-flow: column;
    justify-content: space-between;
    width: 400px;
    display: flex;
    position: absolute;
    inset: 0% 0% 0% auto;
}


.chat-top-div {
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    display: flex;
}

.image-406 {
    height: 20px;
}

.image-407 {
    width: 25px;
}

.link-block-56 {
    width: 25px;
}

/* Chat messages area */
.chat-body-div {
    max-width: 400px;
    padding: 0 10px 0;
    position: relative;
    overflow: visible;
}

.chat-reco-div {
    overflow: scroll;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
    height: calc(100% - 120px);
}

.chat-reco-div::-webkit-scrollbar {
    display: none;
}

.chat-div {
    border: 1px #000;
    margin-bottom: 15px;
}

.dialogue-div {
    padding: 15px 10px 10px 0;
}

/* User message styling */
.user-div {
    flex-flow: row-reverse;
    width: 100%;
    margin-bottom: 25px;
    display: flex;
}

.speech {
    background-color: var(--f3-grey);
    border-radius: 20px;
    width: 75%;
    padding: 10px 15px;
}

.text-block-806 {
    font-family: var(--mulish);
    font-size: 17px;
    line-height: 21px;
}

/* Assistant message styling */
.platform-div {
    justify-content: flex-start;
    align-items: flex-start;
    display: flex;
}

.div-block-1757 {
    background-position: 0 0;
    background-repeat: no-repeat;
    background-size: cover;
    border-radius: 99px;
    width: 25px;
    height: 25px;
    margin-right: 10px;
}

.response-div {
    width: 90%;
}

.paragraph-132 {
    font-family: var(--mulish);
    color: var(--black);
    width: auto;
    font-size: 17px;
    line-height: 21px;
}

/* Chat input area */
.box-bottom-div {
    position: sticky;
    bottom: 0;
    background: #fff;
    width: 100%;
    left: 0;
    right: 0;
    z-index: 100;
}

.chat-area-div {
    border-top: 1px solid #000;
    padding: 10px 10px 0;
}

.form-block-2 {
    margin-bottom: 0;
}

.form-10 {
    position: relative;
}

.text-field-53 {
    width: 100%;
    padding-right: 40px; /* Make room for the send button */
}

.div-block-1754 {
    display: flex;
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
}

.image-413 {
    width: 45px;
}

.chtnote {
    position: relative;
    text-align: center;
    width: 100%;
    font-size: 8px;
    line-height: 1rem;
    padding-bottom: 5px;
    clear: both;
}

/* Chat bubble when minimized */
.chat-bubble {
    position: fixed;
    bottom: 20px;
    right: 20px;
    width: 60px;
    height: 60px;
    background-color: #fff;
    border-radius: 50%;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 1000;
    transition: box-shadow 0.2s ease;
}

.chat-bubble:hover {
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.25);
}

.chat-bubble-icon {
    width: 28px;
    height: 28px;
}

/* Registration/login form elements */
.chat-body-top {
    margin-bottom: 15px;
    margin-top: 10px;
}

.text-block-788 {
    font-size: 21px;
    font-weight: 900;
}

._2-input-div {
    display: flex;
    justify-content: space-between;
}

.tf_textfield-2 {
    border-radius: 6px;
    margin-bottom: 25px;
}

.tf_textfield-2:focus {
    border-color: #6d22ff;
}

.tf_textfield-2::placeholder {
    font-size: 13px;
}

.tf_textfield-2.half {
    width: 48%;
}

.checkout-button-2 {
    font-weight: 700;
    width: 100%;
}

.text-block-865 {
    font-size: 12px;
    margin-top: 10px;
}

/* For disabled state of send button */
.disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* Loading History Indicator */
.loading-history {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 15px 10px;
    gap: 10px;
}

.loading-spinner {
    width: 20px;
    height: 20px;
    border: 2px solid #f3f3f3;
    border-top: 2px solid #2d6a4f;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.loading-text {
    font-size: 13px;
    color: #666;
    font-family: var(--mulish);
}

/* Responsive adjustments */
@media screen and (max-width: 767px) {
    .abubody {
        margin-bottom: 20vh;
        height: 80vh;
        position: fixed;
    }
    
    /* Enhance fixed positioning for mobile */
    .box-bottom-div {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 1000;
        border-top: 1px solid #ccc;
    }
    
    .chat-reco-div {
        height: calc(100vh - 150px);
        padding-bottom: 80px;
    }
    
    .form-10 {
        display: flex;
        align-items: center;
    }
    
    .div-block-1754 {
        position: relative;
        transform: none;
        right: auto;
        top: auto;
        margin-left: 10px;
    }
}

@media (max-width: 479px) {
    .chat-box-div {
        flex-flow: column;
        justify-content: space-between;
        width: 100%;
        display: flex;
        inset: 0%;
    }
    
    .chat-body-div {
        max-width: 1000px;
        padding-top: 0;
        overflow: auto;
    }
    
    .chat-reco-div {
        height: calc(100vh - 170px);
    }
}

@media (min-width: 1024px) {
    .chat-box-div {
        margin-top: 20vh;
        height: 80vh;
        position: fixed;
    }

    .section-6 {
        margin-top: 75px;
    }

    .chat-reco-div {
        height: calc(80vh - 120px);
    }
}

/* Feedback Container Styles - Inside Chat Window */
.feedback-container {
    background: white;
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.feedback-content {
    padding: 15px 20px;
    overflow-y: hidden;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.feedback-header {
    margin-bottom: 12px;
}

.feedback-title {
    font-size: 16px;
    font-weight: 600;
    text-align: center;
    margin: 0;
    color: #000;
    line-height: 1.3;
}

/* Rating Section */
.rating-section {
    display: flex;
    gap: 8px;
    justify-content: center;
    margin: 12px 0 3px;
}

.rating-btn {
    width: 45px;
    height: 45px;
    border: none;
    border-radius: 8px;
    background-color: transparent;
    font-size: 18px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #333;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.rating-btn:hover {
    transform: scale(1.1);
}

.rating-btn.active {
    transform: scale(1.15);
}

.rating-icon {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.rating-label {
    text-align: center;
    font-size: 11px;
    color: #666;
    margin-bottom: 12px;
}

/* Feedback Section */
.feedback-section {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #e0e0e0;
}

.section-title {
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 10px 0;
    text-align: center;
    color: #000;
}

/* Options Grid */
.options-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
}

.option-btn {
    padding: 8px 12px;
    border: 1.5px solid #d0d0d0;
    border-radius: 6px;
    background-color: white;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #000;
    font-weight: 500;
}

.option-btn:hover {
    border-color: #00cc8d;
    background-color: #f8f8f8;
}

.option-btn.active {
    border-color: #00cc8d;
    background-color: #00cc8d;
    color: white;
}

/* Yes/No Buttons */
.yes-no-buttons {
    display: flex;
    gap: 12px;
    justify-content: center;
}

.yn-btn {
    padding: 8px 30px;
    border: 1.5px solid #d0d0d0;
    border-radius: 6px;
    background-color: white;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #000;
}

.yn-btn:hover {
    border-color: #00cc8d;
    background-color: #f8f8f8;
}

.yn-btn.active {
    border-color: #00cc8d;
    background-color: #00cc8d;
    color: #fff
}

/* Textarea */
.feedback-textarea {
    width: 100%;
    min-height: 70px;
    max-height: 70px;
    padding: 10px;
    border: 1.5px solid #d0d0d0;
    border-radius: 6px;
    font-family: inherit;
    font-size: 12px;
    resize: none;
    margin-bottom: 3px;
}

.feedback-textarea:focus {
    outline: none;
    border-color: #00cc8d;
}

.char-count {
    text-align: right;
    font-size: 10px;
    color: #999;
}

/* Action Buttons */
.feedback-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 12px;
}

.btn-submit {
    padding: 10px 20px;
    background-color: #00cc8d;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.btn-submit:hover {
    background-color: #00cc8d;
}

.btn-skip {
    padding: 6px 20px;
    background-color: transparent;
    color: #666;
    border: none;
    font-size: 12px;
    cursor: pointer;
    text-decoration: underline;
}

.btn-skip:hover {
    color: #333;
}

/* Fade Transition */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* Mobile Responsive */
@media (max-width: 479px) {
    .feedback-content {
        padding: 15px;
    }

    .rating-btn {
        width: 45px;
        height: 45px;
        font-size: 18px;
    }

    .options-grid {
        grid-template-columns: 1fr;
    }

    .feedback-title {
        font-size: 16px;
    }
}

</style>
